import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import { useLocale } from 'locale';
import { IUpdateUserProfileVariables, PromotionPlanType, LeadTypeApi3, ISubmitLeadVariables } from 'store/sagas/apiService/types';
import { POC, User } from 'utils/entities';
import { includesNonNumeric, isPhoneNumberValid } from 'utils/formValidationHelpers';
import * as Styled from './styled';
import CloseIcon from 'assets/svg/close.svg';
import WhatsAppIcon from 'assets/svg/whatsapp.svg';
import { TextField } from 'ds/components/input/TextField';
import { Poi } from 'utils/marketplaceRoutes';
import { animated, useSpring, useTransition } from 'react-spring';
import { DURATION_IN_MS, BUTTON_WIDTH } from './styled';
import { usePrevious } from 'hooks/usePrevious';
import TermsLine from 'components/terms-line';

const PHONE_NUMBER_LENGTH = 10;
const RESERVED_SPACE = BUTTON_WIDTH + 16;

const AnimatedChildrenWrapper = animated(Styled.ChildrenWrapper);
const AnimatedCollectNumberForm = animated(Styled.CollectNumberForm);
const AnimatedWhatsAppBtnWrapper = animated(Styled.WhatsAppAnimatedWrapper);

const config = {
  tension: 300,
  friction: 20,
  duration: DURATION_IN_MS,
};

const privateLeadTypes = new Set([ LeadTypeApi3.AGENT_RESIDENTIAL_BULLETIN, LeadTypeApi3.AGENT_COMMERCIAL_BULLETIN, LeadTypeApi3.PRIVATE_COMMERCIAL_BULLETIN, LeadTypeApi3.PRIVATE_RESIDENTIAL_BULLETIN ]);

export interface ContactsWithWhatsAppExpandableProps {
  poi: Poi;
  user: User;
  leadApi3Type: LeadTypeApi3;
  userPhone: string;
  whatsappNumber: string;
  whatsAppHref: string;
  validPhoneNumber: boolean;
  isLoggedInUser?: string;
  children: React.ReactNode;
  onUpdateUser: (data: Pick<IUpdateUserProfileVariables, 'phone'>) => void;
  onSubmitLead: (data: ISubmitLeadVariables) => void;
  onWhatsAppButtonClick?: (poc: POC, phone: string, user: User, contactPhone: string, plan?: PromotionPlanType) => void;
  onWhatsAppFormOpen?: (poc: POC, phone: string, user: User) => void;
  onWhatsAppFormClose?: (poc: POC, phone: string, user: User) => void;
}

export const ContactsWithWhatsAppExpandable = ({
  poi,
  user,
  userPhone,
  whatsAppHref,
  whatsappNumber,
  isLoggedInUser,
  validPhoneNumber,
  children,
  leadApi3Type,
  onSubmitLead,
  onWhatsAppFormOpen,
  onWhatsAppFormClose,
  onUpdateUser,
  onWhatsAppButtonClick,
 }: ContactsWithWhatsAppExpandableProps) => {
  const { t } = useLocale();

  const [ isCollectNumberFormOpen, setIsCollectNumberFormOpen ] = useState(false);
  const [ phoneNumber, setPhoneNumber ] = useState(userPhone);
  const [ errorMessage, setErrorMessage ] = useState('');
  const [ childrenItems, setChildrenItems ] = useState([ children ]);
  const [ formItems, setFormItems ] = useState([]);

  const isInitialRender = useRef(true);
  const parentRef = useRef<HTMLDivElement>(null);

  const previousIsCollectNumberFormOpen = usePrevious(isCollectNumberFormOpen);

  const childrenTransitions = useTransition(childrenItems, null, {
    from: { left: '0px', transform: isInitialRender.current ? 'translateX(0px)' : 'translateX(-150%)' },
    enter: { left: '0px', transform: 'translateX(0px)' },
    leave: { left: '0px', transform: 'translateX(-150%)' },
    config,
  });

  const formTransitions = useTransition(formItems, null, {
    from: { transform: 'translateX(120%)' },
    enter: { transform: 'translateX(0px)' },
    leave: { transform: 'translateX(120%)' },
    config,
  });

  const spring = useSpring({
    left: isCollectNumberFormOpen ? '0%' : '100%',
    transform: isCollectNumberFormOpen ? `translateX(0px)` : `translateX(-100%)`,
    immediate: isInitialRender.current,
    config,
  });

  const poc = _.get(poi, 'poc');
  const promotionPlan = poi.type === 'bulletin' ? poi.promotionPlanType : undefined;

  const closeCollectNumberForm = () => {
    setIsCollectNumberFormOpen(false);
    setPhoneNumber(userPhone);
    setErrorMessage('');
  };

  const handleOpenCollectNumberForm = () => {
    if (onWhatsAppFormOpen) onWhatsAppFormOpen(poc, whatsappNumber, user);
    setIsCollectNumberFormOpen(true);
  };

  const handleCloseCollectNumberForm = () => {
    if (onWhatsAppFormClose) onWhatsAppFormClose(poc, whatsappNumber, user);
    closeCollectNumberForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (includesNonNumeric(newValue)) {
      setErrorMessage(t('contactAgentModal.collectNumberInput.error.nonNumeric'));
    }
    else if (newValue.length === PHONE_NUMBER_LENGTH && !isPhoneNumberValid(newValue)) {
      setErrorMessage(t('contactAgentModal.collectNumberInput.error.invalid'));
    }
    else {
      setErrorMessage('');
    }

    setPhoneNumber(newValue);
  };

  const submitLead = (updateUser: boolean) => {
    if (leadApi3Type && privateLeadTypes.has(leadApi3Type)) {
      onSubmitLead({ leadInput: { phone: phoneNumber, type: leadApi3Type, productId: poi.id, isWhatsApp: true, leadTermsAccepted: true } });
    }
    if (onUpdateUser && updateUser) {
      onUpdateUser({ phone: phoneNumber });
    }
  };

  const handleSubmitCollectNumberForm = () => {
    if (includesNonNumeric(phoneNumber)) {
      setErrorMessage(t('contactAgentModal.collectNumberInput.error.nonNumeric'));
      return;
    }

    if (!isPhoneNumberValid(phoneNumber)) {
      setErrorMessage(t('contactAgentModal.collectNumberInput.error.invalid'));
      return;
    }

    if (phoneNumber) {
      submitLead(!userPhone && Boolean(isLoggedInUser));
    }

    window.open(whatsAppHref, '_blank');
    if (onWhatsAppButtonClick) onWhatsAppButtonClick(poc, whatsappNumber, user, phoneNumber, promotionPlan);
    closeCollectNumberForm();
  };

  useEffect(() => {
    if (isCollectNumberFormOpen && !previousIsCollectNumberFormOpen) {
      setChildrenItems([]);
      setFormItems([ true ]);
    }
    else if (!isCollectNumberFormOpen && previousIsCollectNumberFormOpen) {
      setChildrenItems([ children ]);
      setFormItems([]);
    }
    isInitialRender.current = false;
  }, [ isCollectNumberFormOpen, children, previousIsCollectNumberFormOpen ]);

  return (
    <Styled.Wrapper ref={parentRef}>
      {formTransitions.map(({ key, props }) => (
        <animated.div key={key} style={{ ...props, width: `calc(100% - ${RESERVED_SPACE}px)` }}>
          <AnimatedCollectNumberForm key={key} style={{ alignItems: !!errorMessage ? 'flex-start' : 'center', paddingBottom: '32px' }} >
            <Styled.CloseButton style={{ width: 48 }} mode="ghost-dark" size="extraLarge" onlyIcon onClick={handleCloseCollectNumberForm} type="button">
              <CloseIcon width={16} height={16} />
            </Styled.CloseButton>
            <TextField
                fullWidth
                maxLength={PHONE_NUMBER_LENGTH}
                value={phoneNumber}
                hasError={!!errorMessage}
                errorMessage={errorMessage}
                onChange={handleInputChange}
                placeholder={t('contactAgentModal.collectNumberInput.placeholder')}
            />
          </AnimatedCollectNumberForm>
          <Styled.TermsWrap>
            <TermsLine small={false} />
          </Styled.TermsWrap>
        </animated.div>
      ))}
      {validPhoneNumber ? (
        <AnimatedWhatsAppBtnWrapper isFormOpen={isCollectNumberFormOpen} style={{ ...spring, position: 'absolute' }}>
          <Styled.WhatsAppBtn
            id="whatsAppButton"
            mode="dark"
            size="extraLarge"
            isFormOpen={isCollectNumberFormOpen}
            onClick={isCollectNumberFormOpen ? handleSubmitCollectNumberForm : handleOpenCollectNumberForm}
            disabled={isCollectNumberFormOpen && (!!errorMessage || phoneNumber.length < PHONE_NUMBER_LENGTH)}
            onlyIcon={isCollectNumberFormOpen}
          >
            <WhatsAppIcon width={20} height={20} fill="white"/>
            <span>{t('contactAgentModal.whatsAppButton.label')}</span>
          </Styled.WhatsAppBtn>
        </AnimatedWhatsAppBtnWrapper>
      ) : null}

      {validPhoneNumber ? (
        childrenTransitions.map(({ key, props, item }) => (
          <AnimatedChildrenWrapper key={key} style={props}>
            {item}
          </AnimatedChildrenWrapper>
        ))
      ) : children}
    </Styled.Wrapper>
  );
};
